import React, { useRef, useState, useEffect, useMemo, useCallback } from "react";

import classNames from "classnames";
import { isEqual } from "lodash";
import qs from "qs";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useQueryState } from "react-router-use-location-state";

import style from "../assets/scss/pages/search.module.scss";
import ArticleUrlModifier from "../components/helpers/ArticleUrlModifier";
import NewsSearchResults from "../components/search/NewsSearchResults";
import SearchActionRow from "../components/search/SearchActionRow";
import SearchFilterMenu from "../components/search/SearchFilterMenu";
import useDocumentTitle from "../helpers/useDocumentTitle";
import { selectAccessibleNewsfeedArticleFilters } from "../store/articleFilter/selector";
import { getFilters } from "../store/articleFilter/slice";
import { selectDashboardFilterMenuOpen } from "../store/dashboard/selector";
import { setModalNewsItem } from "../store/dashboard/slice";

function NewsSearchPage() {
  const dispatch = useDispatch();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const filterMenuOpen = useSelector(selectDashboardFilterMenuOpen);
  const filters = useSelector(selectAccessibleNewsfeedArticleFilters, isEqual);
  useDocumentTitle();

  const asideRef = useRef(null);
  const testDefaultFiltersQueryParam = useRef(null);

  const [searchFilters, setSearchFilters] = useState([]);
  const [loadingFilters, setLoadingFilters] = useState(true);

  const queryObject = useMemo(() => {
    return qs.parse(search, { ignoreQueryPrefix: true });
  }, [search]);

  const defaultFiltersQueryParam = useMemo(() => {
    return queryObject.defaultFilters
      ? Array.isArray(queryObject.defaultFilters)
        ? queryObject.defaultFilters
        : [queryObject.defaultFilters]
      : [];
  }, [queryObject.defaultFilters]);

  useEffect(() => {
    if (!filters.length) {
      dispatch(getFilters());
    } else {
      // Clone filters and deactivate them by default
      setSearchFilters(
        filters
          .filter(filter => filter.custom?.properties?.originalId !== "all")
          .map(filter => {
            filter = { ...filter, active: false };

            // If the filter was previously activated on the search page keep it as active
            if (searchFilters.length) {
              const searchFilterPrevState = searchFilters.find(f => f.id === filter.id);
              if (searchFilterPrevState) filter.active = searchFilterPrevState.active;
            }

            // Activate default filter if it is present in the URL query parameters
            if (!searchFilters.length && defaultFiltersQueryParam.length) {
              const searchFilter = defaultFiltersQueryParam.find(
                item => item === filter.title && filter.default
              );
              if (searchFilter) filter.active = true;
            }

            return filter;
          })
      );
      setLoadingFilters(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, dispatch]);

  // Deactivate all filters on search reset
  useEffect(() => {
    if (!defaultFiltersQueryParam.length && searchFilters.length) {
      // Are there any filters activated while the relevant search parameters are not there?
      const activeFilters = searchFilters.filter(filter => filter.active);

      if (activeFilters.length) {
        setSearchFilters(
          searchFilters.map(filter => {
            filter = { ...filter, active: false };
            return filter;
          })
        );
      }
    } else if (defaultFiltersQueryParam.length && searchFilters.length) {
      const filteredFilters = searchFilters.map(filter =>
        defaultFiltersQueryParam.includes(filter.title) && filter.default
          ? {
              ...filter,
              active: true
            }
          : { ...filter, active: false }
      );
      setSearchFilters(filteredFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultFiltersQueryParam]);

  const handleFilterClick = id => {
    const filter = searchFilters.find(f => f.id === id);

    setSearchFilters(
      searchFilters.map(filter =>
        filter.id === id
          ? {
              ...filter,
              active: !filter.active
            }
          : { ...filter }
      )
    );

    // Add active default filters as URL query parameters
    if (!filter.default) return;

    let defaultFilters = [...defaultFiltersQueryParam];

    if (!filter.active) {
      defaultFilters.push(filter.title);
    } else {
      const index = defaultFilters.findIndex(item => item === filter.title);
      if (index !== -1) defaultFilters.splice(index, 1);
    }

    if (defaultFilters.length) {
      queryObject.defaultFilters = defaultFilters;
    } else {
      delete queryObject.defaultFilters;
    }

    navigate(
      {
        pathname: pathname,
        search: `?${qs.stringify(queryObject, { arrayFormat: "repeat" })}`
      },
      { replace: true }
    );
  };

  const onClearFilters = useCallback(() => {
    setSearchFilters(currentSearchFilters =>
      currentSearchFilters.map(filter => ({
        ...filter,
        active: false
      }))
    );

    delete queryObject.defaultFilters;
    navigate(
      {
        pathname: pathname,
        search: `?${qs.stringify(queryObject, { arrayFormat: "repeat" })}`
      },
      { replace: true }
    );
  }, [navigate, pathname, queryObject]);

  return (
    <>
      <ArticleUrlModifier
        service={"news"}
        onHardLoad={article => dispatch(setModalNewsItem(article))}
      />
      <SearchActionRow
        filters={searchFilters}
        loadingFilters={loadingFilters}
        onClearFilters={onClearFilters}
        onFilterClick={id => handleFilterClick(id)}
      />
      <div className={style["container"]}>
        <aside
          className={classNames(style["aside"], { [style["aside--open"]]: filterMenuOpen })}
          ref={asideRef}
        >
          <div className={style["aside__container"]}>
            <SearchFilterMenu
              filters={searchFilters}
              onClearFilters={onClearFilters}
              onFilterClick={id => handleFilterClick(id)}
            />
          </div>
        </aside>
        {!loadingFilters && <NewsSearchResults filters={searchFilters} />}
      </div>
    </>
  );
}

export default NewsSearchPage;
